import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { BarElement, CategoryScale, Chart, Legend, Tooltip } from "chart.js";
import DataLabels from "chartjs-plugin-datalabels";
import Stacked100 from "chartjs-plugin-stacked100";
import { Bar } from "react-chartjs-2";
import { Item } from "reakit/ts/Composite/__utils/types";
import styled from "styled-components";
import { Serie } from "@charty/models";
import { useTheme } from "@glossy/theme";
import { AbstractDate, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { getDatesFromRange } from "@tootools/date";
import { updateBarChartOnHover, useFormatData, useOnClickBar } from "./data";
import { getOptions } from "./options";
import { ChartTooltip } from ".";

Chart.register(
  BarElement,
  CategoryScale,
  Tooltip,
  Legend,
  Stacked100,
  DataLabels
);

export interface ChartProps {
  hovered: string;
  series: Serie[];
  range: Range;
  locale: Locale;
  onClickBar?: (
    uid: Item["id"],
    range: { from: AbstractDate; to: AbstractDate }
  ) => void;
  tooltip?: (serie: Item, total: number) => ReactNode;
  total?: number;
  className?: string;
  chartTooltip?: (props: ChartTooltip[]) => ReactNode;
  isNormalized?: boolean;
  scale?: "logarithmic" | "linear";
  legendSeries?: any;
  timezone: Timezone;
  disableAnimation?: boolean;
  isFullscreen?: boolean;
  metricLabel?: string;
}

const Root = styled.div`
  position: relative;
  height: calc(100% - 36px);
`;

const StackedBarchartChart = ({
  hovered,
  chartTooltip,
  range,
  locale,
  series,
  onClickBar,
  scale,
  isNormalized,
  legendSeries,
  timezone,
  disableAnimation,
  isFullscreen,
  metricLabel,
}: ChartProps) => {
  const chartRef = useRef(null);
  const theme = useTheme();
  const data = useFormatData(series, theme, range);
  const _onClickBar = useOnClickBar(legendSeries, range, onClickBar);

  const options = useMemo(
    () =>
      getOptions(
        getDatesFromRange(range),
        range.interval,
        theme,
        locale,
        timezone,
        onClickBar ? _onClickBar : null,
        series,
        chartTooltip,
        isNormalized,
        scale,
        disableAnimation,
        isFullscreen,
        metricLabel
      ),
    [
      range,
      theme,
      locale,
      timezone,
      onClickBar,
      _onClickBar,
      series,
      chartTooltip,
      isNormalized,
      scale,
      disableAnimation,
      isFullscreen,
      metricLabel,
    ]
  );

  useEffect(() => {
    if (chartRef.current) {
      updateBarChartOnHover(chartRef.current, hovered);
      chartRef.current.update();
    }
  }, [hovered, chartRef]);

  return (
    <Root>
      <Bar data={data} options={options} ref={chartRef} />
    </Root>
  );
};

export default StackedBarchartChart;
